import { ICategoryCommission } from '@/models/commission.model';

export enum CommissionTab {
  CATEGORIES = 'categories',
  HISTORY = 'history',
}

export enum CommissionHistoryFiltersFields {
  DATE_FROM = 'date_from',
  DATE_TO = 'date_to',
  CATEGORY_ID = 'category_id',
  CHANGED_BY_ADMIN_ID = 'changed_by_admin_id',
  PAGE = 'page',
}

export type TCommissionHistoryQuery = {
  date_from?: string;
  date_to?: string;
  category_id?: string;
  changed_by_admin_id?: string;
  page?: number;
};

export interface IFlattenedCategoryCommission extends ICategoryCommission {
  depth: number;
  hasChildren: boolean;
}

export const flattenCategoryCommissionTree = (
  categories: ICategoryCommission[],
  depth = 0,
  seenIds = new Set<string>(),
): IFlattenedCategoryCommission[] =>
  categories.flatMap((category) => {
    if (seenIds.has(category.id)) {
      return [];
    }

    seenIds.add(category.id);

    const children = category.children ?? [];
    const flatItem: IFlattenedCategoryCommission = {
      id: category.id,
      name: category.name,
      commission_percent: category.commission_percent,
      parent_id: category.parent_id,
      depth,
      hasChildren: children.length > 0,
    };

    return [flatItem, ...flattenCategoryCommissionTree(children, depth + 1, seenIds)];
  });

export const buildEditedValuesFromTree = (categories: ICategoryCommission[]): Record<string, number> =>
  flattenCategoryCommissionTree(categories).reduce<Record<string, number>>((acc, category) => {
    acc[category.id] = category.commission_percent;
    return acc;
  }, {});

export const findCategoryInTree = (
  categories: ICategoryCommission[],
  categoryId: string,
): ICategoryCommission | null => {
  for (const category of categories) {
    if (category.id === categoryId) {
      return category;
    }

    const found = findCategoryInTree(category.children ?? [], categoryId);

    if (found) {
      return found;
    }
  }

  return null;
};

export const findCategoryPath = (categories: ICategoryCommission[], categoryId: string): string[] => {
  for (const category of categories) {
    if (category.id === categoryId) {
      return [category.id];
    }

    const childPath = findCategoryPath(category.children ?? [], categoryId);

    if (childPath.length > 0) {
      return [category.id, ...childPath];
    }
  }

  return [];
};

export const collectCategoryAndDescendantIds = (category: ICategoryCommission): string[] => {
  const children = category.children ?? [];

  return [category.id, ...children.flatMap(collectCategoryAndDescendantIds)];
};

export const getCommissionTrendDirection = (oldPercent: number, newPercent: number): 'up' | 'down' | 'equal' => {
  if (newPercent > oldPercent) {
    return 'up';
  }

  if (newPercent < oldPercent) {
    return 'down';
  }

  return 'equal';
};
